import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Analytics - Google Analytics 4 integration
 * Tracks page views and custom events
 * 
 * To enable:
 * 1. Add GA_MEASUREMENT_ID to environment variables
 * 2. Import and use <Analytics /> in App.js inside Router
 */

// Initialize GA4
const initGA4 = (measurementId) => {
  if (typeof window === 'undefined' || !measurementId) return;
  
  // Don't initialize in development unless explicitly enabled
  if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_ENABLE_ANALYTICS) {
    console.log('[Analytics] Disabled in development');
    return;
  }
  
  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
  
  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    page_path: window.location.pathname,
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure'
  });
  
  console.log('[Analytics] GA4 initialized');
};

// Track page views
const trackPageView = (path, title) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href
  });
};

// Track custom events
const trackEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', eventName, params);
};

// Track user properties
const setUserProperties = (properties) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('set', 'user_properties', properties);
};

// Pre-defined event helpers
const analyticsEvents = {
  // Authentication
  login: (method = 'email') => trackEvent('login', { method }),
  signup: (method = 'email') => trackEvent('sign_up', { method }),
  logout: () => trackEvent('logout'),
  
  // Feature usage
  featureUsed: (featureName, context = 'business') => 
    trackEvent('feature_used', { feature_name: featureName, context }),
  
  // Transactions
  transactionCreated: (type, amount, category) => 
    trackEvent('transaction_created', { type, amount, category }),
  
  receiptScanned: (success, extractedAmount) => 
    trackEvent('receipt_scanned', { success, extracted_amount: extractedAmount }),
  
  // Reports
  reportGenerated: (reportType, format) => 
    trackEvent('report_generated', { report_type: reportType, format }),
  
  scheduleC: (action) => 
    trackEvent('schedule_c', { action }),
  
  // Budget & Goals
  budgetCreated: (category, limit) => 
    trackEvent('budget_created', { category, limit }),
  
  goalCreated: (category, target) => 
    trackEvent('goal_created', { category, target }),
  
  goalContribution: (goalId, amount) => 
    trackEvent('goal_contribution', { goal_id: goalId, amount }),
  
  // Invoicing
  invoiceCreated: (total) => 
    trackEvent('invoice_created', { total }),
  
  invoicePaid: (total) => 
    trackEvent('invoice_paid', { total }),
  
  // Business features
  quarterlyTaxCalculated: (quarter, estimatedTax) => 
    trackEvent('quarterly_tax_calculated', { quarter, estimated_tax: estimatedTax }),
  
  profitLossViewed: (period) => 
    trackEvent('profit_loss_viewed', { period }),
  
  // Errors
  error: (errorType, errorMessage) => 
    trackEvent('error', { error_type: errorType, error_message: errorMessage }),
  
  // Performance
  pageLoadTime: (loadTime) => 
    trackEvent('page_load_time', { load_time: loadTime }),
  
  // Engagement
  timeOnPage: (pagePath, duration) => 
    trackEvent('time_on_page', { page_path: pagePath, duration_seconds: duration })
};

/**
 * Analytics Component
 * Automatically tracks page views on route changes
 */
function Analytics() {
  const location = useLocation();
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  
  // Initialize GA4 on mount
  useEffect(() => {
    if (measurementId) {
      initGA4(measurementId);
    }
  }, [measurementId]);
  
  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);
  
  return null; // This component doesn't render anything
}

/**
 * useAnalytics hook for easy event tracking in components
 */
function useAnalytics() {
  return {
    trackEvent,
    trackPageView,
    setUserProperties,
    ...analyticsEvents
  };
}

export { Analytics, useAnalytics, trackEvent, trackPageView, setUserProperties, analyticsEvents };
export default Analytics;
